import { useEffect, useState } from 'react';
import { useNavigate, useRevalidator } from 'react-router';

import { Button } from '@ds/core';
import { Alert, Modal, Stepper } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { DadosDaNovaClinica, Plano } from '@/data/tipos';

const VAZIA: DadosDaNovaClinica = {
    nome: '', apelido: '', documento: '', plano: '', situacao: 'trial',
    unidade: { nome: 'Unidade principal', telefone: '', cep: '', estado: '', cidade: '', bairro: '', rua: '', numero: '', complemento: '' },
    proprietaria: { nome: '', email: '' },
};

/** "Clínica Bem-Estar São José" vira "clinica-bem-estar-sao-jose". */
export function apelidoDe(nome: string): string {
    return nome
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40);
}

const PASSOS = [{ label: 'Cadastro' }, { label: 'Unidade principal' }, { label: 'Proprietária' }];

/**
 * Abrir uma clínica nova, em três passos.
 *
 * A clínica nasce inteira ou não nasce: o tenant, a unidade principal, a
 * proprietária como convite pendente e a assinatura saem de uma função só, numa
 * transação. Os passos são da tela; o banco valida tudo de novo.
 */
export function CriarClinicaModal({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
    const navigate = useNavigate();
    const { revalidate } = useRevalidator();
    const aviso = useAviso();
    const [passo, setPasso] = useState(0);
    const [form, setForm] = useState<DadosDaNovaClinica>(VAZIA);
    const [apelidoEditado, setApelidoEditado] = useState(false);
    const [planos, setPlanos] = useState<Plano[] | null>(null);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        if (!aberto) return;
        setPasso(0);
        setForm(VAZIA);
        setApelidoEditado(false);
        setErro(null);
        let vivo = true;
        dados().faturamento()
            .then((carga) => {
                if (!vivo) return;
                const ativos = carga.planos.filter((p) => p.ativo);
                setPlanos(ativos);
                setForm((f) => ({ ...f, plano: f.plano || ativos[0]?.codigo || '' }));
            })
            .catch((e: unknown) => { if (vivo) setErro(e instanceof Error ? e.message : 'Não foi possível ler os planos.'); });
        return () => { vivo = false; };
    }, [aberto]);

    const unidade = (campo: keyof DadosDaNovaClinica['unidade'], valor: string) =>
        setForm((f) => ({ ...f, unidade: { ...f.unidade, [campo]: valor } }));

    const passoValido = [
        form.nome.trim().length >= 3 && /^[a-z0-9][a-z0-9-]{1,39}$/.test(form.apelido)
            && form.documento.replace(/\D/g, '').length === 14 && Boolean(form.plano),
        form.unidade.nome.trim().length >= 2,
        form.proprietaria.nome.trim().length >= 3 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.proprietaria.email.trim()),
    ][passo] ?? false;

    const criar = async () => {
        setSalvando(true);
        setErro(null);
        try {
            const { clinicaId } = await dados().criarClinica(form);
            /* A clínica existe; o convite é o passo seguinte, e falhar nele não desfaz
               a clínica — só precisa ser dito, para alguém reenviar. */
            try {
                const { email } = await dados().convidarProprietaria(clinicaId);
                aviso({
                    title: 'Clínica aberta',
                    description: email
                        ? `${form.nome} · convite enviado para ${email}`
                        : `${form.nome} · proprietária com convite pendente (a demonstração não envia e-mail)`,
                });
            } catch (e) {
                aviso({
                    title: 'Clínica aberta, convite não enviado',
                    description: e instanceof Error ? e.message : 'Reenvie o convite da proprietária pela ficha da clínica.',
                    tone: 'warning',
                });
            }
            onFechar();
            void revalidate();
            navigate(`/admin/clinicas/${clinicaId}`);
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível abrir a clínica.');
        } finally {
            setSalvando(false);
        }
    };

    const dirty = form.nome.trim().length > 0 || form.documento.trim().length > 0 || form.proprietaria.email.trim().length > 0;

    return (
        <Modal
            open={aberto} onClose={onFechar} width={600} title="Nova clínica"
            description={['Quem é a clínica e em que plano entra.', 'Onde ela atende primeiro.', 'Quem responde por ela.'][passo]}
            dirty={dirty}
            footer={(
                <>
                    {passo > 0 && <Button variant="ghost" onClick={() => setPasso((p) => p - 1)}>Voltar</Button>}
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    {passo < 2 ? (
                        <Button disabled={!passoValido} onClick={() => setPasso((p) => p + 1)}>Continuar</Button>
                    ) : (
                        <Button loading={salvando} disabled={!passoValido} onClick={() => { void criar(); }}>Abrir clínica</Button>
                    )}
                </>
            )}
        >
            <Stepper current={passo} steps={PASSOS} style={{ marginBottom: 18 }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                {passo === 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(220px,100%),1fr))', gap: 14 }}>
                        <FormField label="Nome da clínica" htmlFor="nova-nome" required style={{ gridColumn: '1 / -1' }}>
                            <Input
                                id="nova-nome" value={form.nome}
                                onChange={(v) => setForm((f) => ({ ...f, nome: v, apelido: apelidoEditado ? f.apelido : apelidoDe(v) }))}
                            />
                        </FormField>
                        <FormField label="Apelido" htmlFor="nova-apelido" required hint="Único na plataforma: minúsculas, números e hífen.">
                            <Input
                                id="nova-apelido" value={form.apelido}
                                onChange={(v) => { setApelidoEditado(true); setForm((f) => ({ ...f, apelido: v.toLowerCase() })); }}
                            />
                        </FormField>
                        <FormField label="CNPJ" htmlFor="nova-cnpj" required>
                            <Input id="nova-cnpj" value={form.documento} onChange={(v) => setForm((f) => ({ ...f, documento: v }))} placeholder="00.000.000/0000-00" />
                        </FormField>
                        <FormField label="Plano" htmlFor="nova-plano" required>
                            <Select
                                id="nova-plano" value={form.plano} onChange={(v) => setForm((f) => ({ ...f, plano: v }))}
                                options={(planos ?? []).map((p) => ({
                                    value: p.codigo,
                                    label: `${p.nome} · ${p.precoCentavos === undefined ? 'preço a definir' : moeda(p.precoCentavos)}`,
                                }))}
                            />
                        </FormField>
                        <FormField label="Começa em" htmlFor="nova-situacao" required hint="Trial não gera cobrança.">
                            <Select
                                id="nova-situacao" value={form.situacao}
                                onChange={(v) => setForm((f) => ({ ...f, situacao: v === 'ativa' ? 'ativa' : 'trial' }))}
                                options={[{ value: 'trial', label: 'Trial' }, { value: 'ativa', label: 'Ativa' }]}
                            />
                        </FormField>
                    </div>
                )}

                {passo === 1 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(200px,100%),1fr))', gap: 14 }}>
                        <FormField label="Nome da unidade" htmlFor="nova-unidade" required style={{ gridColumn: '1 / -1' }}>
                            <Input id="nova-unidade" value={form.unidade.nome} onChange={(v) => unidade('nome', v)} />
                        </FormField>
                        <FormField label="Telefone" htmlFor="nova-telefone">
                            <Input id="nova-telefone" value={form.unidade.telefone ?? ''} onChange={(v) => unidade('telefone', v)} />
                        </FormField>
                        <FormField label="CEP" htmlFor="nova-cep">
                            <Input id="nova-cep" value={form.unidade.cep ?? ''} onChange={(v) => unidade('cep', v)} />
                        </FormField>
                        <FormField label="Cidade" htmlFor="nova-cidade">
                            <Input id="nova-cidade" value={form.unidade.cidade ?? ''} onChange={(v) => unidade('cidade', v)} />
                        </FormField>
                        <FormField label="UF" htmlFor="nova-uf">
                            <Input id="nova-uf" value={form.unidade.estado ?? ''} onChange={(v) => unidade('estado', v.toUpperCase().slice(0, 2))} />
                        </FormField>
                        <FormField label="Bairro" htmlFor="nova-bairro">
                            <Input id="nova-bairro" value={form.unidade.bairro ?? ''} onChange={(v) => unidade('bairro', v)} />
                        </FormField>
                        <FormField label="Rua" htmlFor="nova-rua">
                            <Input id="nova-rua" value={form.unidade.rua ?? ''} onChange={(v) => unidade('rua', v)} />
                        </FormField>
                        <FormField label="Número" htmlFor="nova-numero">
                            <Input id="nova-numero" value={form.unidade.numero ?? ''} onChange={(v) => unidade('numero', v)} />
                        </FormField>
                        <FormField label="Complemento" htmlFor="nova-complemento">
                            <Input id="nova-complemento" value={form.unidade.complemento ?? ''} onChange={(v) => unidade('complemento', v)} />
                        </FormField>
                    </div>
                )}

                {passo === 2 && (
                    <>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(220px,100%),1fr))', gap: 14 }}>
                            <FormField label="Nome" htmlFor="nova-dona-nome" required>
                                <Input
                                    id="nova-dona-nome" value={form.proprietaria.nome}
                                    onChange={(v) => setForm((f) => ({ ...f, proprietaria: { ...f.proprietaria, nome: v } }))}
                                />
                            </FormField>
                            <FormField label="E-mail" htmlFor="nova-dona-email" required>
                                <Input
                                    id="nova-dona-email" type="email" value={form.proprietaria.email}
                                    onChange={(v) => setForm((f) => ({ ...f, proprietaria: { ...f.proprietaria, email: v } }))}
                                />
                            </FormField>
                        </div>
                        {/* O convite por e-mail é de outra peça, ainda não ligada aqui:
                            dizer "convite enviado" seria mentir. */}
                        <Alert tone="info" dismissible={false} title="Nenhum e-mail sai daqui">
                            A proprietária nasce como convite pendente. Quando ela criar a conta em “Criar conta” com
                            este e-mail, o vínculo acontece sozinho — e, se ela já tiver conta, acontece na hora. Avise-a
                            pelo canal de sempre.
                        </Alert>
                    </>
                )}
            </div>
        </Modal>
    );
}
